import { useTheme } from '@mui/material';
import { saveAs } from 'file-saver';
import { PDFDownloadLink } from '@react-pdf/renderer';
import {
  BoxSection,
  ButtonPrimary,
  Heading3,
  Heading4,
  Spinner,
  TextM,
} from 'app/components';
import { DownloadIcon, PdfIcon } from 'app/components/Icons';
import { DocumentArea } from '../ApplicationSignaturesTab/styles';
import {
  EApplicationCessionStatus,
  EApplicationStatus,
  TApplicationResponse,
} from 'modules/products/services/hooks/useApplicationsService';
import {
  TProducts,
  TypeProductEmission,
} from 'modules/products/context/ProductsProvider/products.interfaces';
import ApplicationReceiptPDF from '../ApplicationDetailsTab/ApplicationReceiptPDF';
import InternalTransferReceiptPDF from '../InternalTransferReceipt/InternalTransferReceiptPDF';
import { useApplicationsService } from 'modules/products/services/hooks';
import { useCallback, useEffect, useState } from 'react';
import {
  extractReceiptDataFromTemplate,
  receiptTemplateForExtractData,
} from '../InternalTransferReceipt/extractReceiptData';
import { IInternalTransferReceiptPDF } from '../InternalTransferReceipt/interfaces';
import BilletsReceiptPdf from '../ApplicationDetailsTab/BilletsReceiptPdf/BilletsReceiptPdf';
import { ConsigneeOrganType } from 'modules/products/pages/ApplicationRequest/ConsignedStep/interfaces';
import { TCessionConfigResponse } from 'modules/cession/interfaces/cession';
import { useCessionContext } from 'modules/cession/context/CessionProvider';
import { useCessionService } from 'modules/cession/services/useCessionService';
import { useSnackbar } from 'app/hooks/useSnackbar';

interface IApplicationDocumentsProps {
  application: TApplicationResponse;
  product?: TProducts;
}

const buttonStyle = {
  gap: '6px',
  width: 'fit-content',
};

const ApplicationDocuments = ({
  application,
  product,
}: IApplicationDocumentsProps) => {
  /* Hooks */
  const theme = useTheme();
  const { showSnackbar } = useSnackbar();
  const { getCessionConfig } = useCessionService();
  const {
    getApplicationAgreement,
    getSignedApplicationAgreement,
    getSignedMarginConsentFile,
    getSignedEndorsementFile,
    getSignedSecuritizationEndorsementFile,
    loadings,
    getConsigneeReceipt,
    getUnicoDocuments,
    unicoDocuments,
  } = useApplicationsService();
  const {
    state: { hasCessionConfiguration },
  } = useCessionContext();

  /* States */
  const [cessionConfigData, setCessionConfigData] =
    useState<TCessionConfigResponse | null>(null);
  const [internalTransferReceiptData, setInternalTransferReceiptData] =
    useState<IInternalTransferReceiptPDF | null>(null);

  /* Vars */
  const organType = product?.organ_type;

  /* Handlers */
  const handleDownloadAgreement = async (getSignedAgreement = false) => {
    if (getSignedAgreement) {
      const signedAgreementFileRes = await getSignedApplicationAgreement(
        application.id,
      );
      if (signedAgreementFileRes) {
        saveAs(signedAgreementFileRes.url, `signed_ccb_${application.id}.pdf`);
      }
    } else {
      const agreementFileRes = await getApplicationAgreement(application.id);
      saveAs(
        new Blob([agreementFileRes], {
          type: 'application/pdf',
        }),
        `original_ccb_${application.id}.pdf`,
      );
    }
  };

  const handleDownloadSignedEndorsement = async () => {
    const endorsementFileRes = await getSignedEndorsementFile(application.id);
    if (endorsementFileRes) {
      saveAs(
        new Blob([endorsementFileRes], {
          type: 'application/pdf',
        }),
        `signed_endorsement_${application.id}.pdf`,
      );
    }
  };

  const handleDownloadSignedSecuritizationEndorsement = async () => {
    const endorsementFileRes = await getSignedSecuritizationEndorsementFile(
      application.id,
    );
    if (endorsementFileRes) {
      saveAs(
        new Blob([endorsementFileRes], {
          type: 'application/pdf',
        }),
        `signed_endorsement_securitization_${application.id}.pdf`,
      );
    }
  };

  const getSignedMarginConsentFileHandler = async () => {
    const fileResponse = await getSignedMarginConsentFile(
      application.beneficiary_account?.holder.taxpayer_id || '',
    );
    if (fileResponse) {
      saveAs(
        new Blob([fileResponse], {
          type: 'application/pdf',
        }),
        `consentimento_de_margem_${application.id}.pdf`,
      );
    }
  };

  const handleDownloadReceipt = async () => {
    const file = await getConsigneeReceipt(application?.id);
    if (file) {
      saveAs(
        new Blob([file], {
          type: 'application/pdf',
        }),
        `comprovante_averbacao_${application.id}.pdf`,
      );
    }
  };

  const handleDownloadUnicoDocuments = useCallback(async () => {
    const documentsArray = unicoDocuments?.CCB_ASSINADA;

    if (unicoDocuments && documentsArray && documentsArray.length > 0) {
      for (let index = 0; index < documentsArray.length; index++) {
        const attachment = documentsArray[index];

        try {
          const attachmentObj =
            typeof attachment === 'string'
              ? { type: 'DOCUMENT', url: attachment }
              : attachment;

          const typeName = attachmentObj.type?.toLowerCase() || 'documento';
          const fileExtension = attachmentObj.url.includes('.jpeg')
            ? '.jpeg'
            : attachmentObj.url.includes('.jpg')
            ? '.jpg'
            : '.pdf';

          const fileName = `${typeName}_${application.id}${fileExtension}`;

          const link = document.createElement('a');
          link.href = attachmentObj.url;
          link.download = fileName;
          link.target = '_blank';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);

          if (index < documentsArray.length - 1) {
            await new Promise((resolve) => setTimeout(resolve, 1200));
          }
        } catch (error) {
          console.error(`Erro ao baixar documento ${index + 1}:`, error);
        }
      }

      showSnackbar('Download de todos os documentos concluídos!', 'success');
    } else {
      showSnackbar('Nenhum documento disponível para download!', 'info');
    }
  }, [unicoDocuments, application.id]);

  const fetchUnicoDocuments = async (id: string) => {
    await getUnicoDocuments(id);
  };

  /* Effects */
  useEffect(() => {
    if (
      typeof application.disbursement?.receipt === 'string' &&
      application.disbursement?.receipt?.includes('\f')
    ) {
      const internalReceiptExtractedData = extractReceiptDataFromTemplate(
        application.disbursement?.receipt?.split('\f')?.[0],
        receiptTemplateForExtractData,
      ) as unknown as IInternalTransferReceiptPDF;

      setInternalTransferReceiptData(internalReceiptExtractedData);
    }
  }, [application]);

  useEffect(() => {
    const getCessionConfiguration = async () => {
      if (
        application &&
        application.funding?.id &&
        hasCessionConfiguration //add this to avoid request if no cession configuration already found
      ) {
        const cessionConfigDataRequest = await getCessionConfig(
          application.funding?.id || '',
        );

        setCessionConfigData(cessionConfigDataRequest || null);
      }
    };
    getCessionConfiguration();

    fetchUnicoDocuments(application.id);
  }, [application, hasCessionConfiguration]);

  return (
    <BoxSection level="wrapper">
      <Heading3 style={{ color: theme?.palette?.brand.secondary.base }}>
        Documentos
      </Heading3>

      {internalTransferReceiptData && (
        <DocumentArea>
          <PdfIcon />
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <Heading4 style={{ verticalAlign: 'center' }}>
              Comprovante de Pagamento ao Tomador:
            </Heading4>
          </div>
          <PDFDownloadLink
            style={{
              textDecoration: 'none',
            }}
            fileName={`Comprovante_transferencia_${application.id}`}
            document={
              <InternalTransferReceiptPDF data={internalTransferReceiptData} />
            }
          >
            {() => {
              return (
                <ButtonPrimary
                  typeVariant="outline"
                  style={{
                    gap: '6px',
                    width: 'fit-content',
                    color: theme?.palette?.brand.primary.base,
                  }}
                >
                  <DownloadIcon
                    color={theme?.palette?.brand.primary.base as string}
                  />
                  <TextM
                    weight="Semibold"
                    style={{ color: theme?.palette?.brand.primary.base }}
                  >
                    Baixar
                  </TextM>
                </ButtonPrimary>
              );
            }}
          </PDFDownloadLink>
        </DocumentArea>
      )}
      {typeof application.disbursement?.receipt === 'string' && (
        <DocumentArea>
          <PdfIcon />
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <Heading4 style={{ verticalAlign: 'center' }}>
              Comprovante Detalhado:
            </Heading4>
          </div>
          <PDFDownloadLink
            style={{
              textDecoration: 'none',
            }}
            fileName={`Comprovante_${application.id}`}
            document={
              <ApplicationReceiptPDF
                receipt={application.disbursement?.receipt}
              />
            }
          >
            {() => {
              return (
                <ButtonPrimary
                  typeVariant="outline"
                  style={{
                    gap: '6px',
                    width: 'fit-content',
                    color: theme?.palette?.brand.primary.base,
                  }}
                >
                  <DownloadIcon
                    color={theme?.palette?.brand.primary.base as string}
                  />
                  <TextM
                    weight="Semibold"
                    style={{ color: theme?.palette?.brand.primary.base }}
                  >
                    Baixar
                  </TextM>
                </ButtonPrimary>
              );
            }}
          </PDFDownloadLink>
        </DocumentArea>
      )}
      {!!product?.organ_type && application.status === 'ISSUED' && (
        <DocumentArea>
          <PdfIcon />
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <Heading4 style={{ verticalAlign: 'center' }}>
              Comprovante da averbação:
            </Heading4>
          </div>
          <ButtonPrimary
            typeVariant="outline"
            style={{
              gap: '6px',
              width: 'fit-content',
              color: theme?.palette?.brand.primary.base,
            }}
            onClick={() => handleDownloadReceipt()}
          >
            <DownloadIcon
              color={theme?.palette?.brand.primary.base as string}
            />
            <TextM
              weight="Semibold"
              style={{ color: theme?.palette?.brand.primary.base }}
            >
              Baixar
            </TextM>
          </ButtonPrimary>
        </DocumentArea>
      )}

      {product?.type_product_emission === TypeProductEmission.BILLET_PAYMENT &&
        application.status === EApplicationStatus.ISSUED && (
          <DocumentArea>
            <PdfIcon />
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <Heading4 style={{ verticalAlign: 'center' }}>
                Comprovantes de Pagamento:
              </Heading4>
            </div>
            <PDFDownloadLink
              style={{
                textDecoration: 'none',
              }}
              fileName={`Comprovante_${application.id}`}
              document={
                <BilletsReceiptPdf
                  billetResults={application.payment_orders
                    .filter(
                      (payment) =>
                        payment.type === TypeProductEmission.BILLET_PAYMENT &&
                        payment.status === 'SUCCESS' &&
                        payment.billet_info,
                    )
                    .map((payment) => {
                      return payment.billet_info!;
                    })}
                />
              }
            >
              {() => {
                return (
                  <ButtonPrimary
                    typeVariant="outline"
                    style={{
                      gap: '6px',
                      width: 'fit-content',
                      color: theme?.palette?.brand.primary.base,
                    }}
                  >
                    <DownloadIcon
                      color={theme?.palette?.brand.primary.base as string}
                    />
                    <TextM
                      weight="Semibold"
                      style={{ color: theme?.palette?.brand.primary.base }}
                    >
                      Baixar
                    </TextM>
                  </ButtonPrimary>
                );
              }}
            </PDFDownloadLink>
          </DocumentArea>
        )}

      <DocumentArea>
        <PdfIcon />
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <Heading4 style={{ verticalAlign: 'center' }}>
            Contrato Original:{' '}
          </Heading4>
        </div>
        {loadings.isDownloadAgreementLoading ? (
          <Spinner />
        ) : (
          <ButtonPrimary
            typeVariant="outline"
            style={{
              ...buttonStyle,
              color: theme.palette.brand.primary.base,
            }}
            onClick={() => handleDownloadAgreement(false)}
          >
            <DownloadIcon color={theme.palette.brand.primary.base as string} />
            <TextM
              weight="Semibold"
              style={{ color: theme.palette.brand.primary.base }}
            >
              Baixar
            </TextM>
          </ButtonPrimary>
        )}
      </DocumentArea>
      {application?.status &&
        ![
          EApplicationStatus.CANCELED,
          EApplicationStatus.AGREEMENT_RENDERING,
          EApplicationStatus.PENDING_SIGNATURE,
          EApplicationStatus.CREATING_BENEFICIARY_ACCOUNT,
          EApplicationStatus.KYC_PROCESSING,
        ].includes(application?.status as EApplicationStatus) && (
          <DocumentArea>
            <PdfIcon />
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <Heading4 style={{ verticalAlign: 'center' }}>
                Contrato Assinado:{' '}
              </Heading4>
            </div>
            {loadings.isDownloadSignedAgreementLoading ? (
              <Spinner />
            ) : (
              <ButtonPrimary
                typeVariant="outline"
                style={{
                  ...buttonStyle,
                  color: theme.palette.brand.primary.base,
                }}
                onClick={() => handleDownloadAgreement(true)}
              >
                <DownloadIcon
                  color={theme.palette.brand.primary.base as string}
                />
                <TextM
                  weight="Semibold"
                  style={{ color: theme.palette.brand.primary.base }}
                >
                  Baixar
                </TextM>
              </ButtonPrimary>
            )}
          </DocumentArea>
        )}
      {application?.status &&
        [EApplicationStatus.ISSUED].includes(
          application?.status as EApplicationStatus,
        ) && (
          <DocumentArea>
            <PdfIcon />
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <Heading4 style={{ verticalAlign: 'center' }}>
                Endosso Assinado:{' '}
              </Heading4>
            </div>
            {loadings.isDownloadSignedEndorsementLoading ? (
              <Spinner />
            ) : (
              <ButtonPrimary
                typeVariant="outline"
                style={{
                  ...buttonStyle,
                  color: theme.palette.brand.primary.base,
                }}
                onClick={() => handleDownloadSignedEndorsement()}
              >
                <DownloadIcon
                  color={theme.palette.brand.primary.base as string}
                />
                <TextM
                  weight="Semibold"
                  style={{ color: theme.palette.brand.primary.base }}
                >
                  Baixar
                </TextM>
              </ButtonPrimary>
            )}
          </DocumentArea>
        )}

      {application?.cession_status === EApplicationCessionStatus.CEDED &&
        cessionConfigData?.securitization_enabled && (
          <DocumentArea>
            <PdfIcon />
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <Heading4 style={{ verticalAlign: 'center' }}>
                Endosso Assinado (Securitizadora):{' '}
              </Heading4>
            </div>
            {loadings.isDownloadSignedSecuritizationEndorsementLoading ? (
              <Spinner />
            ) : (
              <ButtonPrimary
                typeVariant="outline"
                style={{
                  ...buttonStyle,
                  color: theme.palette.brand.primary.base,
                }}
                onClick={() => handleDownloadSignedSecuritizationEndorsement()}
              >
                <DownloadIcon
                  color={theme.palette.brand.primary.base as string}
                />
                <TextM
                  weight="Semibold"
                  style={{ color: theme.palette.brand.primary.base }}
                >
                  Baixar
                </TextM>
              </ButtonPrimary>
            )}
          </DocumentArea>
        )}
      {organType === ConsigneeOrganType.CT && (
        <DocumentArea>
          <PdfIcon />
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <Heading4 style={{ verticalAlign: 'center' }}>
              Consentimento de margem:{' '}
            </Heading4>
          </div>
          {loadings.isConsignedLoading ? (
            <Spinner />
          ) : (
            <ButtonPrimary
              typeVariant="outline"
              style={{
                ...buttonStyle,
                color: theme.palette.brand.primary.base,
              }}
              onClick={() => getSignedMarginConsentFileHandler()}
            >
              <DownloadIcon
                color={theme.palette.brand.primary.base as string}
              />
              <TextM
                weight="Semibold"
                style={{ color: theme.palette.brand.primary.base }}
              >
                Baixar
              </TextM>
            </ButtonPrimary>
          )}
        </DocumentArea>
      )}

      {unicoDocuments?.CCB_ASSINADA &&
        unicoDocuments.CCB_ASSINADA.length > 0 && (
          <DocumentArea>
            <PdfIcon />
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <Heading4 style={{ verticalAlign: 'center' }}>
                Documentos Unico:
              </Heading4>
            </div>
            {loadings.isDownloadUnicoDocumentsLoading ? (
              <Spinner />
            ) : (
              <ButtonPrimary
                typeVariant="outline"
                style={{
                  ...buttonStyle,
                  color: theme.palette.brand.primary.base,
                }}
                onClick={() => handleDownloadUnicoDocuments()}
              >
                <DownloadIcon
                  color={theme.palette.brand.primary.base as string}
                />
                <TextM
                  weight="Semibold"
                  style={{ color: theme.palette.brand.primary.base }}
                >
                  Baixar
                </TextM>
              </ButtonPrimary>
            )}
          </DocumentArea>
        )}
    </BoxSection>
  );
};

export default ApplicationDocuments;
